import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { switchMap, takeWhile, tap } from 'rxjs/operators';

import {
  BroadcastCall,
  BroadcastCallContact,
  BroadcastCallContactStatus,
  BroadcastCallReport,
  BroadcastCallService,
  broadcastCallStatusView,
} from 'src/app/shared/services/broadcast-call.service';
import { PaginatedResult } from 'src/app/shared/model/paginated-result';
import { backendToBrasiliaIso } from 'src/app/shared/utils/brasilia-date';
import { ConfirmationService } from 'src/app/shared/components/confirmation-modal/confirmation.service';
import { ToastService } from 'src/app/shared/components/toast/toast.service';
import { environment } from 'src/environments/environment';

interface WebhookPlatform {
  key: string;
  label: string;
  active: boolean;
  sample: string;
}

/** Status "em andamento" (dispara a barra de progresso e o polling automático). */
const IN_PROGRESS_STATUSES = ['Processando', 'Ligando'];

@Component({
  selector: 'app-view-broadcast-call',
  templateUrl: './view-broadcast-call.component.html',
  styleUrls: ['./view-broadcast-call.component.scss'],
})
export class ViewBroadcastCallComponent implements OnInit, OnDestroy {

  broadcastId: string;
  broadcast: BroadcastCall;
  report: BroadcastCallReport;
  contacts: PaginatedResult<BroadcastCallContact[]>;
  loading = true;
  loadingContacts = false;
  page = 1;
  perPage = 15;
  phone = '';

  /**
   * Total de contatos do broadcast (sem o filtro de telefone), usado como denominador da
   * barra de progresso. Só é atualizado quando `loadContacts()` roda sem filtro — se não, um
   * filtro de telefone restrito faria a barra de progresso "encolher" o total.
   */
  totalContacts: number | null = null;

  webhooksOpen = false;
  expandedPlatform: string | null = null;

  // 6 ativas (parser real no backend) + 6 stubs (no-op por ora — mantém URL
  // válida pra paridade com Call4U, parser pode ser portado on-demand).
  //
  // TODO(F6 do CR MR !453): essa lista hardcoded espelha
  // BroadcastCallIntegrationsService::PLATFORMS_ACTIVE/PLATFORMS_STUB no
  // backend. Quando uma plataforma virar ativa (ou nova for adicionada),
  // ambos os lados precisam ser atualizados. Migrar pra um endpoint
  // GET /api/broadcast-calls/integrations/platforms que devolve {key,
  // label, active, sample} e renderizar dinamicamente. Não bloqueia esse
  // MR — ficou como item separado na próxima rodada de melhorias.
  readonly webhookPlatforms: WebhookPlatform[] = [
    { key: 'default',         label: 'Default',         active: true,  sample: `{\n  "number": "11912345678",\n  "name": "Cliente"\n}` },
    { key: 'smsfunnel',       label: 'SMS Funnel',      active: true,  sample: `[\n  { "phone": "11912345678", "name": "Cliente" }\n]` },
    { key: 'activecampaign',  label: 'ActiveCampaign',  active: true,  sample: `{\n  "contact": {\n    "first_name": "João",\n    "last_name": "Silva",\n    "phone": "11912345678"\n  }\n}` },
    { key: 'monetizze',       label: 'Monetizze',       active: true,  sample: `{\n  "comprador": [\n    { "nome": "Cliente", "numero": "11912345678" }\n  ]\n}` },
    { key: 'perfectpay',      label: 'PerfectPay',      active: true,  sample: `{\n  "customer": {\n    "full_name": "Cliente",\n    "phone_area_code": "11",\n    "phone_number": "912345678"\n  }\n}` },
    { key: 'voxuy',           label: 'Voxuy',           active: true,  sample: `{\n  "clientName": "Cliente",\n  "clientPhoneNumber": "11912345678"\n}` },
    { key: 'appmax',          label: 'AppMax',          active: false, sample: '' },
    { key: 'braip',           label: 'Braip',           active: false, sample: '' },
    { key: 'kiwify',          label: 'Kiwify',          active: false, sample: '' },
    { key: 'yampi',           label: 'Yampi',           active: false, sample: '' },
    { key: 'ticto',           label: 'Ticto',           active: false, sample: '' },
    { key: 'rdstation',       label: 'RD Station',      active: false, sample: '' },
  ];

  private pollSubscription: Subscription;
  subscriptions: Subscription[] = [];
  /** A busca de contatos em andamento — uma por vez (ver `loadContacts()`). */
  private contactsRequest: Subscription | null = null;

  constructor(
    private route: ActivatedRoute,
    private broadcastCallService: BroadcastCallService,
    private confirmationService: ConfirmationService,
    private toastService: ToastService,
  ) { }

  ngOnInit(): void {
    this.broadcastId = this.route.snapshot.paramMap.get('id');
    this.loadData();
    this.loadContacts();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.contactsRequest?.unsubscribe();
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
    }
  }

  /**
   * O backend devolve as datas do broadcast (created_at, scheduled_date,
   * scheduled_end_date) como string "naive" 'Y-m-d H:i:s' SEM offset, que
   * representam UTC (o front grava via toISOString). Delegamos ao util
   * compartilhado, que as interpreta como UTC; combine com o pipe
   * `date : '-0300'` para renderizar em horário de Brasília em qualquer fuso.
   */
  brasiliaIso(value: string | null | undefined): string | null {
    return backendToBrasiliaIso(value);
  }

  loadData(): void {
    this.loading = true;
    this.subscriptions.push(
      this.broadcastCallService.getById(this.broadcastId).subscribe({
        next: data => {
          this.broadcast = data.broadcast;
          this.report = data.report;
          this.loading = false;
          this.startPollingIfActive();
        },
        error: () => {
          this.loading = false;
          this.toastService.error('Erro ao carregar broadcast de voz');
        },
      })
    );
  }

  /**
   * Review do MR !558 (ação 12): uma busca por vez. Filtrar/paginar de novo antes da resposta
   * anterior chegar cancela a anterior — senão a que respondesse por último (às vezes a do
   * filtro/página ANTIGOS) sobrescrevia a tabela e, sem filtro, o `totalContacts` da barra de
   * progresso.
   */
  loadContacts(): void {
    this.loadingContacts = true;
    this.contactsRequest?.unsubscribe();
    this.contactsRequest = this.broadcastCallService
      .getContacts(this.broadcastId, this.page, this.perPage, this.phone || undefined)
      .subscribe({
        next: contacts => {
          this.contacts = contacts;
          if (!this.phone) {
            this.totalContacts = contacts.total;
          }
          this.loadingContacts = false;
        },
        error: () => {
          this.loadingContacts = false;
          this.toastService.error('Erro ao carregar contatos');
        },
      });
  }

  filterByPhone(): void {
    this.page = 1;
    this.loadContacts();
  }

  clearPhoneFilter(): void {
    this.phone = '';
    this.page = 1;
    this.loadContacts();
  }

  pageChanged(page: number): void {
    this.page = page;
    this.loadContacts();
  }

  get isScheduled(): boolean {
    return this.broadcast?.status?.status === 'Pendente';
  }

  get isActive(): boolean {
    const status = this.broadcast?.status?.status;
    return status === 'Pendente' || status === 'Processando' || status === 'Ligando';
  }

  /** "Processando"/"Ligando" — mostra a barra de progresso e dispara o polling automático. */
  get isInProgress(): boolean {
    return IN_PROGRESS_STATUSES.includes(this.broadcast?.status?.status);
  }

  get isPaused(): boolean {
    return this.broadcast?.status?.status === 'Pausado';
  }

  get canCancel(): boolean {
    return this.isActive || this.isPaused;
  }

  /** % de contatos já discados (sent) sobre o total da lista, ou `null` sem total conhecido. */
  get progressPercent(): number | null {
    if (!this.isInProgress || !this.totalContacts) { return null; }
    const sent = this.broadcast?.total_sent ?? 0;
    return Math.min(100, Math.round((sent / this.totalContacts) * 100));
  }

  startPollingIfActive(): void {
    if (!this.broadcast) { return; }
    const active = IN_PROGRESS_STATUSES.includes(this.broadcast.status?.status);
    if (!active) { return; }

    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
    }

    // Poll every 30 seconds when broadcast is active, auto-stop when no longer active
    this.pollSubscription = interval(30000).pipe(
      switchMap(() => this.broadcastCallService.getById(this.broadcastId)),
      tap(data => {
        this.broadcast = data.broadcast;
        this.report = data.report;
      }),
      takeWhile(() => IN_PROGRESS_STATUSES.includes(this.broadcast.status?.status)),
    ).subscribe();
  }

  pauseBroadcast(): void {
    this.subscriptions.push(
      this.broadcastCallService.pause(this.broadcastId).subscribe({
        next: () => {
          this.toastService.success('Broadcast pausado com sucesso!');
          this.loadData();
        },
        error: err => this.toastService.error(err.error?.message || 'Erro ao pausar broadcast'),
      })
    );
  }

  resumeBroadcast(): void {
    this.subscriptions.push(
      this.broadcastCallService.resume(this.broadcastId).subscribe({
        next: () => {
          this.toastService.success('Broadcast retomado com sucesso!');
          this.loadData();
        },
        error: err => this.toastService.error(err.error?.message || 'Erro ao retomar broadcast'),
      })
    );
  }

  confirmCancel(): void {
    this.confirmationService.show(
      `Deseja realmente cancelar o broadcast "${this.broadcast.name}"?`,
      () => this.cancelBroadcast()
    );
  }

  cancelBroadcast(): void {
    this.subscriptions.push(
      this.broadcastCallService.cancel(this.broadcastId).subscribe({
        next: () => {
          this.toastService.success('Broadcast cancelado com sucesso!');
          this.loadData();
        },
        error: err => this.toastService.error(err.error?.message || 'Erro ao cancelar broadcast'),
      })
    );
  }

  exportCsv(): void {
    this.subscriptions.push(
      this.broadcastCallService.exportCsv(this.broadcastId).subscribe({
        next: blob => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `broadcast-call-${this.broadcastId}.csv`;
          a.click();
          window.URL.revokeObjectURL(url);
        },
        error: () => this.toastService.error('Erro ao exportar CSV'),
      })
    );
  }

  /** Status com o "Aguardando retorno" (07/10/2026). Ver `broadcastCallStatusView`. */
  get statusLabel(): string {
    return broadcastCallStatusView(this.broadcast).label;
  }

  /** Converte a cor do status para o tom do `m8v-status-pill` (ver mesmo método na lista). */
  get statusTone(): string {
    const cls = broadcastCallStatusView(this.broadcast).cls;
    switch (cls) {
      case 'warning': return 'warn';
      case 'info': return 'cool';
      case 'success': return 'ok';
      case 'danger': return 'danger';
      default: return 'muted';
    }
  }

  /** Ligações que já saíram e ainda esperam o retorno do PABX — ver `awaiting_callbacks`. */
  get awaitingCallbacks(): number {
    return this.broadcast?.awaiting_callbacks || 0;
  }

  getContactStatusLabel(contact: BroadcastCallContact): string {
    // O backend já devolve o rótulo pronto (mesma função que alimenta o CSV) — usar o
    // valor dele evita que tela e export divirjam. O cálculo abaixo é só fallback para
    // deploy escalonado (front no ar antes do backend).
    if (contact.status_label) { return contact.status_label; }

    const status = contact.pabx_status;
    if (!status) {
      return contact.status === BroadcastCallContactStatus.Error
        ? 'Erro / Telefone inválido'
        : 'Aguardando';
    }

    if (['NOANSWER', 'BUSY', 'CONGESTION', 'CHANUNAVAIL'].includes(status)) {
      return 'Recusada / Nao Atendida';
    }

    if (status === 'CANCELLED') {
      return 'Cancelado';
    }

    if (status === 'ANSWER') {
      if (contact.dtmf_response) {
        return 'Atendida com Digitacao';
      }
      if ((contact.listened_percentage ?? 0) >= 95) {
        return 'Atendida e Escutada ate o Fim';
      }
      return 'Atendida e Desligada';
    }

    return status;
  }

  /** Tom do `m8v-status-pill` para a coluna Status da tabela de contatos. */
  contactStatusTone(contact: BroadcastCallContact): string {
    const label = this.getContactStatusLabel(contact);
    if (label.includes('Fim') || label.includes('Digitacao') || label.includes('Digitação')) { return 'ok'; }
    if (label.startsWith('Recusada') || label.startsWith('Erro') || label === 'Cancelado') { return 'danger'; }
    if (label === 'Aguardando') { return 'muted'; }
    return 'warn';
  }

  formatDuration(seconds: number | null): string {
    if (!seconds) { return '-'; }
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  // Em PROD/HOM/BETA `publicWebhookBase` aponta pra mesma URL pública do
  // `apiUrl`. Foi separado pra deixar claro que essa URL é usada por terceiros
  // e não deve ser substituída por um proxy interno num futuro deploy. O
  // template renderiza um aviso quando production=false, evitando que admin em
  // DEV cole essa URL na Monetizze e quebre o webhook do cliente.
  isWebhookBasePublic = environment.production;

  webhookUrl(action: 'add' | 'remove', platform: string): string {
    const hash = this.broadcast?.webhook_hash;
    if (!hash) { return ''; }
    const base = environment.publicWebhookBase || environment.apiUrl;
    return `${base}/integrations/${action}/${hash}/${platform}`;
  }

  togglePlatform(key: string): void {
    this.expandedPlatform = this.expandedPlatform === key ? null : key;
  }

  copyWebhookUrl(url: string): void {
    if (!url) { return; }
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(
        () => this.toastService.success('URL copiada!'),
        () => this.toastService.error('Falha ao copiar'),
      );
      return;
    }
    // Fallback para browsers sem clipboard API
    const ta = document.createElement('textarea');
    ta.value = url;
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      this.toastService.success('URL copiada!');
    } catch {
      this.toastService.error('Falha ao copiar');
    }
    document.body.removeChild(ta);
  }
}
